import {
  Box,
  Button,
  FormControlLabel,
  MenuItem,
  Switch,
  Typography,
} from "@mui/material";
import Select from "components/common/Select";
import { Family, FormErrors } from "./types";
import AdvisorSelect from "../creation/AdvisorSelect";
import { useContext, useMemo, useState } from "react";
import { CurrentUserContext } from "../context/RequireAuth";
import { hasRole, ROLE_ADMIN } from "../../common/userUtils";
import { AccountStub, Advisor } from "protogen/common_pb";
import { UserAvatar } from "../common/CurrentUserAvatar";
import Checkbox from "../common/Checkbox";
import { defaultPermissions, permissionExpirationErrorKey } from "./utils";
import { ChevronDown } from "lucide-react";
import useIsMobile from "components/hooks/useIsMobile";
import LinkRouter from "components/navigation/LinkRouter";
import { FamilyStatus } from "protogen/advisors_service_pb";
import PermissionExpirationPicker from "./PermissionExpirationPicker";

const AddDelegate = ({ onAdd }: { onAdd: (a: AccountStub) => void }) => {
  const [addingDelegate, setAddingDelegate] = useState<boolean>(false);
  // Add button
  // trigger drop down
  // add delegate with default permissions
  if (!addingDelegate) {
    return (
      <Button
        variant="text"
        sx={{ alignSelf: "start", height: "unset" }}
        onClick={() => setAddingDelegate(true)}
      >
        Add collaborator
      </Button>
    );
  }
  return (
    <AdvisorSelect
      title="Add Collaborator"
      disabled={false}
      error={null}
      selectedAdvisor={null}
      onChange={(v, a) => {
        setAddingDelegate(false);
        v && a && onAdd(a);
      }}
    />
  );
};

interface Props {
  familyData: Family;
  setFamilyData: (f: Family) => void;
  errors: FormErrors | null;
  primaryAdvisor: Advisor;
}

export default ({
  familyData,
  setFamilyData,
  errors,
  primaryAdvisor, // advisorPermissions,
}: Props) => {
  const currentUser = useContext(CurrentUserContext);
  const sortedPermissions = useMemo(
    () =>
      [...familyData.permissions].sort(
        (a, b) => Number(!!b.active) - Number(!!a.active),
      ),
    [familyData.permissions],
  );
  const [selectedPermissionAdvisorRef, setSelectedPermissionAdvisorRef] =
    useState<string | null>(sortedPermissions[0]?.advisorRef || null);
  const selectedPermission = familyData.permissions.find(
    (p) => (p.advisorRef || "") === selectedPermissionAdvisorRef,
  );
  const selectedPermissionActive =
    selectedPermission?.reactivate ||
    (selectedPermission?.active && !selectedPermission?.deactivate);
  const expirationError = selectedPermission
    ? errors?.[permissionExpirationErrorKey(selectedPermission.advisorRef)]
    : undefined;
  const isMobile = useIsMobile();
  const expirationPicker = selectedPermissionActive && selectedPermission && (
    <PermissionExpirationPicker
      expiration={selectedPermission.expiration}
      required={!selectedPermission.ref}
      error={expirationError}
      onChange={(expiration) => {
        setFamilyData({
          ...familyData,
          permissions: familyData.permissions.map((p) =>
            p.advisorRef === selectedPermission.advisorRef
              ? { ...p, expiration }
              : p,
          ),
        });
      }}
    />
  );
  const permissionSwitch = selectedPermission && (
    <Switch
      checked={!!selectedPermissionActive}
      size="small"
      onChange={(e) => {
        setFamilyData({
          ...familyData,
          permissions: familyData.permissions.map((p) => {
            if (p.advisorRef !== selectedPermission.advisorRef) {
              return p;
            } else {
              return {
                ...p,
                ...(e.target.checked
                  ? { reactivate: true }
                  : { deactivate: true }),
              };
            }
          }),
        });
      }}
    />
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "16px",
      }}
    >
      <Typography variant={"h3"}>Advisor details</Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Typography variant={"bodyHeavy"}>Primary Advisor</Typography>
          <Box
            display={"flex"}
            flexDirection={"row"}
            gap={"12px"}
            alignItems="center"
          >
            {primaryAdvisor && <UserAvatar user={primaryAdvisor} size={40} />}
            <Typography variant={"bodyHeavy"}>
              {primaryAdvisor?.displayName}
            </Typography>
          </Box>
        </Box>
        {familyData.status === FamilyStatus.ACTIVE && (
          <LinkRouter
            to={`/tools/family-rematch?family=${encodeURIComponent(
              familyData?.ref || "",
            )}`}
          >
            <Button sx={{ whiteSpace: "nowrap" }} variant="text">
              Request advisor rematch
            </Button>
          </LinkRouter>
        )}
      </Box>
      {(hasRole(currentUser, ROLE_ADMIN) ||
        currentUser.ref === primaryAdvisor.ref) && (
        <>
          {selectedPermission && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  width: isMobile ? "100%" : "auto",
                  flexDirection: isMobile ? "column" : "row",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    width: isMobile ? "100%" : "auto",
                  }}
                >
                  <Select
                    key={familyData.permissions.length}
                    variant="outlined"
                    sx={{
                      width: isMobile ? "auto" : "250px",
                      flex: isMobile ? "1 1 auto" : "none",
                      minWidth: 0,
                      ".MuiSelect-select": {
                        marginTop: 0,
                      },
                    }}
                    value={selectedPermissionAdvisorRef}
                    renderValue={() => {
                      const permission = familyData.permissions.find(
                        (p) =>
                          (p.advisorRef || "") === selectedPermissionAdvisorRef,
                      );
                      return (
                        <Typography variant="body">
                          {permission?.account?.displayName}
                        </Typography>
                      );
                    }}
                    onChange={(e) =>
                      setSelectedPermissionAdvisorRef(e.target.value as string)
                    }
                    IconComponent={ChevronDown}
                  >
                    {sortedPermissions.map((permission) => {
                      const permissionActive =
                        permission.reactivate ||
                        (permission.active && !permission.deactivate);
                      return (
                        <MenuItem
                          value={permission.advisorRef || ""}
                          key={permission.advisorRef || ""}
                        >
                          {!permissionActive && "[Inactive] "}
                          {permission.account?.displayName}
                        </MenuItem>
                      );
                    })}
                  </Select>
                  {isMobile && (
                    <Box
                      sx={{
                        display: "flex",
                        flex: "0 0 auto",
                        alignItems: "center",
                      }}
                    >
                      {permissionSwitch}
                    </Box>
                  )}
                </Box>
                <Box
                  sx={{
                    ...(!selectedPermissionActive && {
                      opacity: 0.7,
                    }),
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    minWidth: "1px",
                    flex: "1 1 1px",
                    gap: "12px",
                    flexWrap: "wrap",
                  }}
                >
                  {Object.keys(selectedPermission.actions)
                    .sort()
                    .map((key) => (
                      <Box
                        key={key}
                        display="flex"
                        sx={{
                          width: "calc(50% - 6px)",
                        }}
                      >
                        <FormControlLabel
                          key={key}
                          label={key}
                          disabled={!selectedPermissionActive}
                          control={
                            <Checkbox
                              sx={{ padding: "0 10px 0 0" }}
                              checked={selectedPermission.actions[key]}
                              onChange={(e) => {
                                setFamilyData({
                                  ...familyData,
                                  permissions: familyData.permissions.map(
                                    (p) => {
                                      if (
                                        p.advisorRef !==
                                        selectedPermission.advisorRef
                                      ) {
                                        return p;
                                      } else {
                                        return {
                                          ...p,
                                          actions: {
                                            ...p.actions,
                                            [key]: e.target.checked,
                                          },
                                        };
                                      }
                                    },
                                  ),
                                });
                              }}
                            />
                          }
                        />
                      </Box>
                    ))}
                  {isMobile && expirationPicker && (
                    <Box sx={{ display: "flex", width: "100%" }}>
                      {expirationPicker}
                    </Box>
                  )}
                </Box>
                {!isMobile && (
                  <Box
                    sx={{
                      alignSelf: "center",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "flex-end",
                      gap: "8px",
                    }}
                  >
                    {expirationPicker}
                    {permissionSwitch}
                  </Box>
                )}
              </Box>
            </Box>
          )}
          <AddDelegate
            onAdd={(account: AccountStub) => {
              setFamilyData({
                ...familyData,
                permissions: [
                  ...familyData.permissions,
                  defaultPermissions(account),
                ],
              });
              setSelectedPermissionAdvisorRef(account.ref);
            }}
          />
        </>
      )}
    </Box>
  );
};
